import React, { useState, useEffect } from 'react'
import { Button, Card, CARD_THEME, Select } from '@st-operations/uikit/src/v2/index.js'

import { logErrorAsync } from '_lib/utility'
import CountryDistribution from '_components/country-distribution/index.jsx'
import UsZipcodeDistribution from '_components/us-zipcode-distribution/index.jsx'
import {
  getGeoCountryStatistics,
  getGeoStateStatistics,
  getGeoCountyStatistics,
} from '_services/data-explorer'

import { useEventFeedsContext } from '../../../context/index.jsx'
import GeoSideBar from '../geo-side-bar/index.jsx'
import PeriodSelectStored from '_components/period-select-stored/index.jsx'
import { useRequestConfig } from '_hooks'
import IndexBaselineSelect, {
  FIXED_VALUES,
  FIXED_VALUES_OPTIONS,
} from '_components/index-baseline-select/index.jsx'
import { getGeoPostalCodeStatistics } from '_services/data-explorer.js'
import { TIME_PERIOD_VALUES } from '_components/period-select'
import SettingsToggle from '_components/settings-toggle'
import capitalizeFirstLetter from '_lib/captalizeFirstLetter'

import './styles.scss'
import { ExportButton } from '_components/export-button'

const REGIONS = {
  GLOBAL: 'Global',
  US_STATES: 'US States',
  US_COUNTIES: 'US Counties',
  POSTAL_CODES: 'Postal Codes',
}

const MAPPING_FOR_TOP_TITLES = {
  [REGIONS.GLOBAL]: 'Countries',
  [REGIONS.US_STATES]: 'States',
  [REGIONS.US_COUNTIES]: 'Counties',
  [REGIONS.POSTAL_CODES]: 'Postal Codes',
}

export const CALCULATION_METHODS = {
  EVENTS: 'Events',
  PER_CAPITA: 'Per Capita',
  INDEX: 'Index',
  UNIQUE_USERS: 'Unique Users',
}

export const CALCULATION_METHODS_DESCRIPTION = {
  [CALCULATION_METHODS.EVENTS]: 'The total number of Events in a Geo',
  [CALCULATION_METHODS.PER_CAPITA]:
    "The total number of events in a Geo divided by it's population",
  [CALCULATION_METHODS.INDEX]: 'A comparison of the feed to the baseline',
  [CALCULATION_METHODS.UNIQUE_USERS]: 'Total number of Unique Users',
}

export const GEO_TIME_PERIOD_VALUES = {
  MONTH_1: 1,
  MONTH_3: 3,
  MONTH_6: 6,
  YEAR_1: 12,
  CUSTOM: -1,
}

export const GEO_TIME_PERIOD_NAMES = {
  [GEO_TIME_PERIOD_VALUES.MONTH_1]: '1 month',
  [GEO_TIME_PERIOD_VALUES.MONTH_3]: '3 months',
  [GEO_TIME_PERIOD_VALUES.MONTH_6]: '6 months',
  [GEO_TIME_PERIOD_VALUES.YEAR_1]: '1 year',
  [GEO_TIME_PERIOD_VALUES.CUSTOM]: 'Custom',
}

const defaultOverrides = {
  period: GEO_TIME_PERIOD_VALUES.MONTH_1,
}

const Geo = () => {
  const { start, end, interval, period } = useRequestConfig('highlightsGeo', defaultOverrides)

  const [ranking, setRanking] = useState({
    [REGIONS.GLOBAL]: [],
    [REGIONS.US_STATES]: [],
    [REGIONS.US_COUNTIES]: [],
  })
  const [loading, setLoading] = useState(false)
  const [region, setRegion] = useState(REGIONS.GLOBAL)
  const [calculationMethod, setCalculationMethod] = useState(CALCULATION_METHODS.EVENTS)
  const [indexBaseline, setIndexBaseline] = useState(FIXED_VALUES_OPTIONS[0])
  const [countrySelection, setCountrySelection] = useState()
  const [position, setPosition] = useState({ coordinates: [0, 0], zoom: 1 })

  const onCountryGeographySelectionChanged = (geo) => {
    setCountrySelection(geo)
  }

  const { data, id } = useEventFeedsContext()

  useEffect(() => {
    const fetchData = async () => {
      if (data._id || id === 'discovery') {
        const requestConfig = {
          attributes: data.attributes,
          filters: data.filters,
          aggregations: data.aggregations,
          start,
          end,
          baselineId:
            indexBaseline.value !== FIXED_VALUES.NO_FILTERS &&
            indexBaseline.value !== FIXED_VALUES.DEFAULT_FILTERS
              ? indexBaseline.value
              : undefined,
          type: calculationMethod === CALCULATION_METHODS.UNIQUE_USERS ? 'users' : undefined,
        }

        setLoading(true)
        try {
          let response
          if (region === REGIONS.GLOBAL) {
            response = await getGeoCountryStatistics(requestConfig)
          } else if (region === REGIONS.US_STATES) {
            response = await getGeoStateStatistics(requestConfig)
          } else if (region === REGIONS.US_COUNTIES) {
            response = await getGeoCountyStatistics(requestConfig)
          }

          const countries = Object.values(response.countries || {})
          const states = Object.values(response.states || {})
          const counties = Object.values(response.counties || {})

          setRanking({
            [REGIONS.GLOBAL]: countries,
            [REGIONS.US_STATES]: states,
            [REGIONS.US_COUNTIES]: counties,
          })
        } catch (error) {
          logErrorAsync(error)
          toast.error(error.message)
        }
        setLoading(false)
      }
    }
    fetchData()
  }, [data, region, indexBaseline, calculationMethod === CALCULATION_METHODS.UNIQUE_USERS])

  const [postalCodeStatistics, setPostalCodeStatistics] = useState()
  useEffect(() => {
    if (!countrySelection) {
      setPostalCodeStatistics()
      return
    }

    const requestConfig = {
      attributes: data.attributes,
      filters: data.filters,
      aggregations: data.aggregations,
      start,
      end,
      country: countrySelection.properties.ISO_A2,
    }
    setLoading(true)
    getGeoPostalCodeStatistics(requestConfig).then((response) => {
      setPostalCodeStatistics(response)
      setLoading(false)
    })
  }, [countrySelection, start, end, data?.filters])

  function handleBack() {
    setCountrySelection()
    setPosition({ coordinates: [0, 0], zoom: 1 })
  }

  return (
    <section className="scale-and-coverage__container">
      <div className="data-preview-highlights">
        <Card
          key="geo-panel"
          className="data-preview-highlights__main-card de-geo"
          theme={CARD_THEME.NO_SHADOW}
        >
          <div className="data-preview-highlights__main-card-title de-geo-header">
            <div className="de-geo-header-title">
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <h2>Geo •&nbsp;</h2>
                <span>{calculationMethod}</span>
              </div>
              <div>{CALCULATION_METHODS_DESCRIPTION[calculationMethod]}</div>
            </div>
            <div style={{ flex: 1 }} />
            <If condition={countrySelection}>
              <Button style={{ margin: 0 }} onClick={handleBack}>
                Back
              </Button>
            </If>
            <If condition={!countrySelection}>
              <div className="de-geo-header__controls">
                <SettingsToggle
                  label={`${calculationMethod}, ${region}, ${capitalizeFirstLetter(
                    interval || ''
                  )}, ${
                    period !== TIME_PERIOD_VALUES.CUSTOM
                      ? GEO_TIME_PERIOD_NAMES[period]
                      : start + ' - ' + end
                  }`}
                >
                  <Select
                    style={{ marginBottom: 10 }}
                    label="Calculation Method"
                    options={[
                      { label: 'Events', value: CALCULATION_METHODS.EVENTS },
                      { label: 'Per Capita', value: CALCULATION_METHODS.PER_CAPITA },
                      { label: 'Index', value: CALCULATION_METHODS.INDEX },
                      { label: 'Unique Users', value: CALCULATION_METHODS.UNIQUE_USERS },
                    ]}
                    value={calculationMethod}
                    onChange={({ value }) => setCalculationMethod(value)}
                    useReactSelect
                  />
                  <Select
                    style={{ marginBottom: 10 }}
                    label="Region Selected"
                    options={[
                      { label: 'Global', value: REGIONS.GLOBAL },
                      { label: 'US States', value: REGIONS.US_STATES },
                      { label: 'US Counties', value: REGIONS.US_COUNTIES },
                    ]}
                    value={region}
                    onChange={({ value }) => setRegion(value)}
                    useReactSelect
                  />
                  <PeriodSelectStored
                    id={id}
                    visualizationsPath="highlightsGeo"
                    customOptions={Object.keys(GEO_TIME_PERIOD_VALUES).map((key) => ({
                      label: GEO_TIME_PERIOD_NAMES[GEO_TIME_PERIOD_VALUES[key]],
                      value: GEO_TIME_PERIOD_VALUES[key],
                    }))}
                    defaultOverrides={{ period: GEO_TIME_PERIOD_VALUES.MONTH_1 }}
                    vertical
                  />
                  <If condition={calculationMethod === CALCULATION_METHODS.INDEX}>
                    <IndexBaselineSelect
                      style={{ width: '100%', marginBottom: 0 }}
                      indexBaseline={indexBaseline}
                      onIndexBaselineChange={(value) => setIndexBaseline(value)}
                    />
                  </If>
                </SettingsToggle>
                <ExportButton
                  data={ranking[region]}
                  filename={`geo-${calculationMethod}-${region}-${start}-${end}`}
                >
                  Export data
                </ExportButton>
              </div>
            </If>
          </div>
          <If condition={region === REGIONS.GLOBAL}>
            <CountryDistribution
              className="data-preview-highlights__main-card-map"
              calculationMethod={calculationMethod}
              loading={loading}
              data={ranking[REGIONS.GLOBAL]}
              geographySelection={countrySelection}
              onGeographySelectionChanged={onCountryGeographySelectionChanged}
              postalCodeData={postalCodeStatistics}
              position={position}
              onPositionChange={setPosition}
              onBack={handleBack}
              style={{ flex: 1, width: 'unset' }}
            />
          </If>
          <If condition={region === REGIONS.US_STATES}>
            <UsZipcodeDistribution
              className="data-preview-highlights__main-card-map"
              calculationMethod={calculationMethod}
              loading={loading}
              data={ranking[REGIONS.US_STATES]}
              type="states"
            />
          </If>
          <If condition={region === REGIONS.US_COUNTIES}>
            <UsZipcodeDistribution
              className="data-preview-highlights__main-card-map"
              calculationMethod={calculationMethod}
              loading={loading}
              data={ranking[REGIONS.US_COUNTIES]}
              type="counties"
            />
          </If>
        </Card>
        ,
        <GeoSideBar
          key="geoSideBar"
          calculationMethod={calculationMethod}
          loading={loading}
          ranking={
            countrySelection
              ? Object.values(postalCodeStatistics?.zipCodes || {}).map((pc) => ({
                  ...pc,
                  name: pc.id,
                }))
              : ranking[region].map((r) =>
                  region === REGIONS.US_COUNTIES ? { ...r, name: `${r.name}` } : r
                )
          }
          topTitle={
            countrySelection
              ? MAPPING_FOR_TOP_TITLES[REGIONS.POSTAL_CODES]
              : MAPPING_FOR_TOP_TITLES[region]
          }
        />
      </div>
    </section>
  )
}

export default Geo
